import * as z from 'zod';

import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import {
	embroideryConfigurationFromFormData,
	embroideryConfigurationSchema,
	filterOptionEmbroideriesByBrandIds,
	getConfiguredEmbroidery,
	getDefaultEmbroideryConfiguration,
	getItemsForEmbroideryConfiguration,
} from '../../embroidery-collection/configuration';
import type {ItemForPriceFragment} from '../../item/query';
import {productColorConfigurationSchema} from '../../product-color/configuration';
import type {ProductAdapter} from '../adapter';
import type {ProductDescription} from '../description';
import {resolveProductImageSettings} from '../product-image-settings';
import {type ProductImageSide} from '../ProductImage';
import type {Overlay} from '../ProductImageStack';
import {
	getDefaultOverlayPlacement,
	getDefaultWatermarkPlacement,
	getOverlayPlacement,
	type ProductImageStackDescription,
} from '../ProductImageStack';
import type {UnionProductCoreFragment} from '../query';

import {cabFloorMatEdgingColorConfigurationFromFormData} from './edging/configuration';
import type {
	CabFloorMatMaterialConfiguration,
	CabFloorMatMaterialConfigurationValidationResult,
} from './material/configuration';
import {
	cabFloorMatMaterialConfigurationFromFormData,
	cabFloorMatMaterialConfigurationSchema,
	getInitialCabFloorMatMaterialConfiguration,
} from './material/configuration';
import type {CabFloorMatMaterialCoreFragment} from './material/query';
import {DOCUMENT_TYPE_CAB_FLOOR_MAT_PRODUCT} from './schema/names';
import {
	getBaseCabFloorMatProductBrandsById,
	getFiltersMatchingCabFloorMatProduct,
} from './filtering';
import type {CabFloorMatProductCoreFragment, CabFloorMatProductFragment} from './query';

export const cabFloorMatConfigurationSchema = z.object({
	material: cabFloorMatMaterialConfigurationSchema.optional(),
	edgingColor: productColorConfigurationSchema.optional(),
	embroidery: embroideryConfigurationSchema.optional(),
});

export type CabFloorMatConfiguration = z.infer<typeof cabFloorMatConfigurationSchema>;

export function parseCabFloorMatConfiguration(value: unknown): CabFloorMatConfiguration | null {
	const result = cabFloorMatConfigurationSchema.safeParse(value);

	if (!result.success) {
		console.warn('Failed to parse cab shoe mat product configuration', result.error);
		return null;
	}

	return result.data;
}

export function isCabFloorMatProduct<P extends UnionProductCoreFragment>(
	product: P,
): product is Extract<P, {_type: typeof DOCUMENT_TYPE_CAB_FLOOR_MAT_PRODUCT}> {
	return product._type === DOCUMENT_TYPE_CAB_FLOOR_MAT_PRODUCT;
}

export function getFilteredEmbroideryOptionForCabFloorMat(product: CabFloorMatProductCoreFragment) {
	if (!product.settings?.embroidery) {
		return null;
	}

	// Filter embroideries to only those matching the product's brands:
	const allowedBrandIds = Array.from(getBaseCabFloorMatProductBrandsById(product).keys());
	return filterOptionEmbroideriesByBrandIds(product.settings.embroidery, allowedBrandIds);
}

/**
 * Returns the simplest (but still valid) configuration of the product.
 */
export function getDefaultCabFloorMatConfiguration(
	product: CabFloorMatProductCoreFragment,
): CabFloorMatConfiguration {
	const defaultConfig: CabFloorMatConfiguration = {};

	if (!product.settings) {
		return defaultConfig;
	}

	const materials = product.settings?.materials;
	if (materials) {
		defaultConfig.material = getInitialCabFloorMatMaterialConfiguration(materials);
	}

	const embroidery = getFilteredEmbroideryOptionForCabFloorMat(product);
	if (embroidery?.available) {
		defaultConfig.embroidery = getDefaultEmbroideryConfiguration(embroidery);
	}

	return defaultConfig;
}

export function cabFloorMatConfigurationFromFormData(
	product: CabFloorMatProductCoreFragment,
	formData: FormData,
): CabFloorMatConfiguration {
	const config: CabFloorMatConfiguration = {};

	const colors = product.settings?.materials;
	if (colors) {
		config.material = cabFloorMatMaterialConfigurationFromFormData(
			colors,
			'material',
			formData,
		);
	}

	const embroidery = getFilteredEmbroideryOptionForCabFloorMat(product);
	if (embroidery?.available) {
		config.embroidery = embroideryConfigurationFromFormData(embroidery, 'embroidery', formData);
	}

	const edgingColor = product.settings?.edgingColor;
	if (edgingColor?.edgingColors?.length) {
		config.edgingColor = cabFloorMatEdgingColorConfigurationFromFormData(
			edgingColor.edgingColors,
			'edgingColor',
			formData,
		);
	}

	return config;
}

export function getItemsForCabFloorMatConfiguration(
	product: CabFloorMatProductCoreFragment,
	configuration: CabFloorMatConfiguration,
): ItemForPriceFragment[] {
	const items: ItemForPriceFragment[] = [];

	// Add base product items:
	items.push(...(product.settings?.items ?? []));

	// Add configured option items:
	const embroidery = getFilteredEmbroideryOptionForCabFloorMat(product);
	if (embroidery?.available && configuration.embroidery) {
		const embroideryItems = getItemsForEmbroideryConfiguration(
			embroidery,
			configuration.embroidery,
		);
		items.push(...embroideryItems);
	}

	const edgingColor = product.settings?.edgingColor;
	if (
		edgingColor &&
		edgingColor.item &&
		edgingColor.edgingColors?.length &&
		configuration.edgingColor
	) {
		items.push(edgingColor.item);
	}

	return items;
}

export type CabFloorMatConfigurationValidationResult = {
	materialError?: CabFloorMatMaterialConfigurationValidationResult;
	isValid: boolean;
};

export function validateCabFloorMatConfiguration(
	product: CabFloorMatProductCoreFragment,
	configuration: CabFloorMatConfiguration,
): CabFloorMatConfigurationValidationResult {
	const result: CabFloorMatConfigurationValidationResult = {isValid: true};

	const configuredMaterial = getConfiguredMaterial(
		product.settings?.materials ?? [],
		configuration.material ?? {},
	);

	if (!configuredMaterial?.materialColor) {
		result.isValid = false;
		result.materialError = result.materialError ?? {};
		result.materialError.materialColorError = {
			message: 'Du må velge en materialfarge.',
		};
	}

	if (
		configuredMaterial.material?.stitchingColors?.length &&
		!configuredMaterial.stitchingColor
	) {
		result.isValid = false;
		result.materialError = result.materialError ?? {};
		result.materialError.stitchingColorError = {
			message: 'Du må velge en sømfarge.',
		};
	}

	return result;
}

export function isValidCabFloorMatConfiguration(
	product: CabFloorMatProductCoreFragment,
	configuration: CabFloorMatConfiguration,
) {
	const validation = validateCabFloorMatConfiguration(product, configuration);
	return validation.isValid;
}

function getConfiguredMaterial(
	materials: CabFloorMatMaterialCoreFragment[],
	config: CabFloorMatMaterialConfiguration,
) {
	const material = materials.find(
		(color) => color.materialColor?._id && color.materialColor._id === config.materialColorId,
	);

	const materialColor = material?.materialColor ?? null;
	const stitchingColor = material?.stitchingColors?.find(
		(stitchingColor) => stitchingColor._id === config.stitchingColorId,
	);

	return {
		material,
		materialColor,
		stitchingColor,
	};
}

export function getImageStackForCabFloorMatConfiguration<Side extends ProductImageSide>(
	side: Side,
	product: CabFloorMatProductCoreFragment,
	configuration: CabFloorMatConfiguration | null,
): ProductImageStackDescription | null {
	if (side !== 'top') {
		// For cab shoe trays, we only have a top image:
		return null;
	}

	if (!configuration || !product.settings) {
		return null;
	}

	// Fetch the most specific (enabled) product image settings:
	const imageSettings = resolveProductImageSettings(product)?.imageSettings;

	if (!imageSettings) {
		return null;
	}

	const overlays: Overlay[] = [];

	// Resolved the configured color and its color properties:
	const {materialColor: configuredMaterialColor, stitchingColor: configuredSitchingColor} =
		getConfiguredMaterial(product.settings?.materials ?? [], configuration.material ?? {});

	// Add base image, if configured:
	let baseImage = imageFromSanityImage(imageSettings.baseImage);
	if (configuredMaterialColor || configuredSitchingColor) {
		// First try to match the material color:
		if (configuredMaterialColor) {
			for (const baseImageForVariantColors of imageSettings.baseImagesForVariantColors ||
				[]) {
				if (baseImageForVariantColors.materialColor?._ref === configuredMaterialColor._id) {
					const baseMaterialImage = imageFromSanityImage(baseImageForVariantColors.image);
					if (baseMaterialImage) {
						baseImage = baseMaterialImage;
						continue;
					}
				}
			}
		}

		// Then see if we have a match for both material and stitching color:
		if (configuredMaterialColor && configuredSitchingColor) {
			for (const baseImageForVariantColors of imageSettings.baseImagesForVariantColors ||
				[]) {
				if (
					baseImageForVariantColors.materialColor?._ref === configuredMaterialColor._id &&
					baseImageForVariantColors.stitchingColor?._ref === configuredSitchingColor._id
				) {
					const baseMaterialImage = imageFromSanityImage(baseImageForVariantColors.image);
					if (baseMaterialImage) {
						baseImage = baseMaterialImage;
					}
				}
			}
		}
	}

	if (!baseImage) {
		// If there's no base image, we don't need to consider the overlays either:
		return null;
	}

	// Add edge image, if configured:
	let edgeImage = imageFromSanityImage(imageSettings.edgeImage);
	const configuredEdgingColor = configuration?.edgingColor ?? configuredMaterialColor?._id; // We use the material color is no edging color is explicitly configured
	if (configuredEdgingColor) {
		for (const edgeImageForEdgeColor of imageSettings.edgeImagesForEdgeColor || []) {
			if (edgeImageForEdgeColor.edgeColor?._ref === configuredEdgingColor) {
				const overlayImage = imageFromSanityImage(edgeImageForEdgeColor.image);
				if (overlayImage) {
					edgeImage = overlayImage;
				}
			}
		}
	}

	if (edgeImage) {
		overlays.push({
			_type: 'imageOverlay',
			image: edgeImage,
			placement: getDefaultOverlayPlacement(),
		});
	}

	// Add embroidery overlay, if configured:
	const embroidery = getFilteredEmbroideryOptionForCabFloorMat(product);
	if (embroidery?.available && configuration?.embroidery) {
		const placement = imageSettings.embroideryImagePlacement;

		const {configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (configuredEmbroidery?.image) {
			const overlayImage = imageFromSanityImage(configuredEmbroidery.image);
			if (overlayImage) {
				overlays.push({
					_type: 'imageOverlay',
					image: overlayImage,
					placement: getOverlayPlacement(placement),
				});
			}
		}
	}

	// Add watermark overlay, if present:
	const watermark = product.settings?.watermark;
	if (watermark) {
		const watermarkImage = imageFromSanityImage(watermark);
		if (watermarkImage) {
			overlays.push({
				_type: 'imageOverlay',
				image: watermarkImage,
				placement: getDefaultWatermarkPlacement(),
			});
		}
	}

	return {
		image: baseImage,
		altText: null,
		overlays,
	};
}

export function getProductNameForCabFloorMatConfiguration(
	product: CabFloorMatProductCoreFragment,
	_configuration: CabFloorMatConfiguration,
) {
	return `${product.name}`;
}

export function getDescriptionForCabFloorMatConfiguration(
	product: CabFloorMatProductCoreFragment,
	configuration: CabFloorMatConfiguration,
): ProductDescription {
	const desc: ProductDescription = [];

	// NOTE: Outputting description in "production order" (i.e. the order that the options are
	//       applied to the product) as requested by Traktorutstyr.

	const {materialColor, stitchingColor} = getConfiguredMaterial(
		product.settings?.materials ?? [],
		configuration.material ?? {},
	);

	if (materialColor) {
		desc.push({
			title: 'Materialfarge',
			value: materialColor?.name ?? materialColor.hex ?? materialColor._id ?? 'Ukjent farge',
		});
	}

	if (stitchingColor) {
		desc.push({
			title: 'Sømfarge',
			value:
				stitchingColor?.name ?? stitchingColor.hex ?? stitchingColor._id ?? 'Ukjent farge',
		});
	}

	const embroidery = getFilteredEmbroideryOptionForCabFloorMat(product);
	if (embroidery?.available && configuration.embroidery) {
		const {configuredCollection, configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (configuredCollection && configuredEmbroidery) {
			desc.push({
				title: 'Broderi',
				value: [
					{
						title: 'Broderisamling',
						value: configuredCollection.name ?? 'Samling uten navn',
					},
					{
						title: 'Navn',
						value: configuredEmbroidery.name ?? 'Broderi uten navn',
					},
				],
			});
		}
	}

	const edgingColor = product.settings?.edgingColor;

	if (edgingColor && edgingColor.edgingColors?.length && configuration.edgingColor) {
		const configuredEdgingColor = edgingColor.edgingColors.find(
			(c) => c._id === configuration.edgingColor,
		);
		if (configuredEdgingColor) {
			desc.push({
				title: 'Tilpasset kantfage',
				value:
					configuredEdgingColor.name ??
					configuredEdgingColor.hex ??
					configuredEdgingColor._id ??
					'Ukjent farge',
			});
		}
	}

	return desc;
}

export const cabFloorMatProductAdapter: ProductAdapter<
	CabFloorMatProductFragment,
	CabFloorMatProductCoreFragment
> = {
	getName: getProductNameForCabFloorMatConfiguration,
	getItems: getItemsForCabFloorMatConfiguration,
	getImages: getImageStackForCabFloorMatConfiguration,
	getDescription: getDescriptionForCabFloorMatConfiguration,
	getMatchingFilters: getFiltersMatchingCabFloorMatProduct,
	isValidConfig: isValidCabFloorMatConfiguration,
	getDefaultConfig: getDefaultCabFloorMatConfiguration,
	parseConfigFromFormData: cabFloorMatConfigurationFromFormData,
	parseConfigFromJSON: parseCabFloorMatConfiguration,
};
